import React, { useEffect, useRef, useState } from 'react';
import { renderSkuWithSoftBreaks } from '../../../utils/skuSoftBreak';
import ProductImage from '../../shared/ProductImage';
import SplitQuantityModal from './SplitQuantityModal';
import {
  MAX_SPLIT_BOXES,
  SPLIT_HOLD_MODES,
  SPLIT_LINE_KINDS,
  addSplitBox,
  closeSplitBox,
  moveLineToNextBox,
  moveUnitsToNextBox,
  nextBoxIndex,
  splitLineToNextBox,
  type SplitBoxes,
  type SplitHoldMode,
  type SplitLine,
} from './splitShipmentBoxes';
import './split-shipment.css';

export const SPLIT_HOLD_MS = 1000;

interface SplitShipmentViewProps {
  /** From `buildSplitLines`: one line per standalone SKU and one per distinct bundle. */
  readonly lines: readonly SplitLine[];
  readonly boxes: SplitBoxes;
  readonly holdMode: SplitHoldMode;
  readonly onChange: (boxes: SplitBoxes) => void;
}

interface PendingSplit {
  readonly boxIndex: number;
  readonly itemId: string;
}

function boxLabelFor(boxIndex: number): string {
  return `Box ${boxIndex + 1}`;
}

function SplitShipmentView({ lines, boxes, holdMode, onChange }: SplitShipmentViewProps): React.ReactElement {
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdFiredRef = useRef(false);
  const boxesRef = useRef(boxes);
  boxesRef.current = boxes;
  const [pendingSplit, setPendingSplit] = useState<PendingSplit | null>(null);

  const clearHold = (): void => {
    if (holdTimerRef.current != null) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  useEffect(() => clearHold, []);

  const startHold = (boxIndex: number, itemId: string): void => {
    clearHold();
    holdFiredRef.current = false;
    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null;
      holdFiredRef.current = true;
      if (holdMode === SPLIT_HOLD_MODES.QUANTITY) {
        setPendingSplit({ boxIndex, itemId });
        return;
      }
      onChange(splitLineToNextBox(boxesRef.current, boxIndex, itemId));
    }, SPLIT_HOLD_MS);
  };

  const handleTap = (boxIndex: number, itemId: string): void => {
    if (holdFiredRef.current) {
      holdFiredRef.current = false;
      return;
    }
    onChange(moveLineToNextBox(boxes, boxIndex, itemId));
  };

  const pendingItem = pendingSplit != null ? lines.find((line) => line.id === pendingSplit.itemId) : undefined;
  const pendingTotal = pendingSplit != null ? boxes[pendingSplit.boxIndex]?.[pendingSplit.itemId] ?? 0 : 0;

  const canAddBox = boxes.length < MAX_SPLIT_BOXES;
  const columnCount = canAddBox ? boxes.length + 1 : boxes.length;

  return (
    <div className="split-shipment">
      <div className={`split-shipment-board split-shipment-board-cols-${columnCount}`}>
        {boxes.map((box, boxIndex) => {
          const boxLabel = boxLabelFor(boxIndex);
          const boxLines = lines.filter((line) => (box[line.id] ?? 0) > 0);
          return (
            <section key={boxIndex} className="split-shipment-column" aria-label={boxLabel}>
              <div className="split-shipment-column-header">
                <h2 className="split-shipment-column-title">{boxLabel}</h2>
                {boxIndex > 0 ? (
                  <button
                    type="button"
                    className="split-shipment-close-box"
                    onClick={() => onChange(closeSplitBox(boxes, boxIndex))}
                    aria-label={`Close ${boxLabel}`}
                  >
                    ×
                  </button>
                ) : null}
              </div>
              <ul className="split-shipment-chips">
                {boxLines.map((line) => {
                  const quantity = box[line.id];
                  const isBundle = line.kind === SPLIT_LINE_KINDS.BUNDLE;
                  return (
                    <li key={line.id}>
                      <button
                        type="button"
                        className={`split-shipment-chip${isBundle ? ' split-shipment-chip-bundle' : ''}`}
                        onPointerDown={() => startHold(boxIndex, line.id)}
                        onPointerUp={clearHold}
                        onPointerLeave={clearHold}
                        onPointerCancel={clearHold}
                        onContextMenu={(event) => event.preventDefault()}
                        onClick={() => handleTap(boxIndex, line.id)}
                        aria-label={`${line.sku}${isBundle ? ' bundle' : ''}, qty ${quantity}, in ${boxLabel}`}
                      >
                        <span className="split-shipment-chip-thumb">
                          <ProductImage sku={line.sku} alt={line.name || line.sku} />
                        </span>
                        <span className="split-shipment-chip-sku">{renderSkuWithSoftBreaks(line.sku)}</span>
                        <span className="split-shipment-chip-qty">{quantity}</span>
                        {isBundle ? (
                          <span className="split-shipment-chip-components">
                            {line.components.map((component) => (
                              <span key={component.sku} className="split-shipment-chip-component">
                                <span className="split-shipment-chip-component-sku">
                                  {renderSkuWithSoftBreaks(component.sku)}
                                </span>
                                <span className="split-shipment-chip-component-qty">
                                  {component.quantityPerBundle * quantity}
                                </span>
                              </span>
                            ))}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
        {canAddBox ? (
          <button
            type="button"
            className="split-shipment-add-box"
            onClick={() => onChange(addSplitBox(boxes))}
          >
            + Add a box
          </button>
        ) : null}
      </div>
      {pendingSplit != null && pendingItem != null && pendingTotal > 0 ? (
        <SplitQuantityModal
          sku={pendingItem.sku}
          name={pendingItem.name}
          fromLabel={boxLabelFor(pendingSplit.boxIndex)}
          toLabel={boxLabelFor(nextBoxIndex(pendingSplit.boxIndex, boxes.length))}
          total={pendingTotal}
          onCancel={() => setPendingSplit(null)}
          onConfirm={(moveQty) => {
            onChange(moveUnitsToNextBox(boxes, pendingSplit.boxIndex, pendingSplit.itemId, moveQty));
            setPendingSplit(null);
          }}
        />
      ) : null}
    </div>
  );
}

export default SplitShipmentView;
